import { forwardRef } from 'react';
import { useButton, useSelect } from 'react-aria';
import { useSelectState } from 'react-stately';

import { useObjectRef } from '@react-aria/utils';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  label?: string;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      options,
      value,
      onChange,
      error,
      label,
      description,
      placeholder,
      disabled,
      className,
      name,
    },
    forwardedRef
  ) => {
    const ref = useObjectRef(forwardedRef);
    const state = useSelectState({
      selectedKey: value,
      onSelectionChange: (key) => onChange?.(key as string),
      isDisabled: disabled,
    });

    const { labelProps, triggerProps } = useSelect(
      {
        label,
        description,
        errorMessage: error,
        isDisabled: disabled,
      },
      state,
      ref
    );

    const { buttonProps } = useButton(triggerProps, ref);

    return (
      <div className='w-full'>
        {label && (
          <label
            {...labelProps}
            className='block text-sm font-medium text-gray-700 mb-1'
          >
            {label}
          </label>
        )}
        <div className='relative'>
          <button
            {...buttonProps}
            type='button'
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-red-500 focus-visible:ring-red-500',
              className
            )}
          >
            <select
              ref={ref}
              name={name}
              disabled={disabled}
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              className='w-full bg-transparent focus:outline-none'
            >
              {placeholder && (
                <option value='' disabled>
                  {placeholder}
                </option>
              )}
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </button>
        </div>
        {description && (
          <p className='mt-1 text-sm text-gray-500'>{description}</p>
        )}
        {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
